<template>
  <Layout :errors="errors" :nav-items="aspectMenuItems" :toasts="toasts">
    <div class="card rounded-corner">
      <div class="card-body position-relative">
        <button
          class="btn btn-primary position-absolute top-0 end-0 mt-3 me-3 ps-5"
          type="submit"
          @click="updatePermissions(route)"
        >
          <i class="fa-solid fa-floppy-disk position-absolute h4 lh-1 start-0 ms-2"></i>
          <span>Änderungen speichern</span>
        </button>
        <div class="model-title-bar">
          <h5 class="card-title text-primary fw-bold text-uppercase">Zugriffsverwaltung</h5>
        </div>
        <p class="card-text">
          Hier lassen sich Berechtigungen für den Zugriff einzelner Rollen auf Inhalte der Datenbank einstellen. Alle
          Benutzer, denen die jeweilige Rolle zugewiesen ist, verfügen über die hier angezeigten Berechtigungen.
        </p>
        <div class="row border-bottom border-top">
          <div class="col p-2 text-center">Datenklasse</div>
          <div v-for="(name, role) in roles" :key="role" :class="getRoleBackgroundColor(role) + ' col text-center p-2'">
            {{ name }}
          </div>
        </div>
        <div v-for="(data, resourceType) in resourceTypes" :key="resourceType" class="row border-bottom">
          <div class="col p-2 align-self-center">
            <colored-icon
              :color="'black'"
              :icon-class="data.icon"
              :shadow="false"
              style="font-size: 1.25rem"
            ></colored-icon
            >&nbsp;{{ data.name }}
          </div>
          <div v-for="(name, role) in roles" :key="role" :class="getRoleBackgroundColor(role) + ' col p-2'">
            <div class="row m-0">
              <div class="col text-center px-2" style="font-size: 0.75rem">
                <span
                  :class="getStatusClass(resourceType, 'read', role)"
                  @click="togglePermission(resourceType, 'read', role)"
                >
                  <i
                    :style="permissionIsEditable(resourceType, 'read', role) ? 'cursor: pointer;' : ''"
                    class="bi bi-eye-fill"
                    style="font-size: 1.25rem"
                  >
                  </i>
                </span>
              </div>
              <div class="col text-center px-2" style="font-size: 0.75rem">
                <span
                  :class="getStatusClass(resourceType, 'update', role)"
                  @click="togglePermission(resourceType, 'update', role)"
                >
                  <i
                    :style="permissionIsEditable(resourceType, 'update', role) ? 'cursor: pointer;' : ''"
                    class="bi bi-pencil-square"
                    style="font-size: 1.25rem"
                  >
                  </i>
                </span>
              </div>
              <div class="col text-center px-2" style="font-size: 0.75rem">
                <span
                  :class="getStatusClass(resourceType, 'create', role)"
                  @click="togglePermission(resourceType, 'create', role)"
                >
                  <i
                    :style="permissionIsEditable(resourceType, 'create', role) ? 'cursor: pointer;' : ''"
                    class="bi bi-plus-square"
                    style="font-size: 1.25rem"
                  >
                  </i>
                </span>
              </div>
              <div class="col text-center px-2" style="font-size: 0.75rem">
                <span
                  :class="getStatusClass(resourceType, 'delete', role)"
                  @click="togglePermission(resourceType, 'delete', role)"
                >
                  <i
                    :style="permissionIsEditable(resourceType, 'delete', role) ? 'cursor: pointer;' : ''"
                    class="bi bi-trash"
                    style="font-size: 1.25rem"
                  >
                  </i>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import ColoredIcon from '@kanrisha/components/Icons/ColoredIcon';
import Layout from '@kanrisha/Pages/Layout';
import { router } from '@inertiajs/vue3';
import { ref } from 'vue';

export default {
  components: {
    ColoredIcon,
    Layout,
  },
  props: {
    aspectMenuItems: { type: Object, default: () => {} },
    resourceTypes: { type: Object, default: () => {} },
    roles: { type: Object, default: () => {} },
    permissions: { type: Object, default: () => {} },
    oldPermissions: { type: Object, default: () => {} },
    toasts: { type: Object, default: () => {} },
    errors: { type: Object, default: () => {} },
    canEditAdmin: { type: Boolean, default: false },
  },
  setup(props) {
    const localPermissions = ref(props.permissions);
    const localOldPermissions = ref(props.oldPermissions);

    const getRoleBackgroundColor = (role) => {
      switch (role) {
        case 'guest':
          return 'bg-danger bg-opacity-10';
        case 'user':
        case 'owner':
          return 'bg-warning bg-opacity-10';
        case 'admin':
        case 'tech':
          return 'bg-success bg-opacity-10';
        default:
          return '';
      }
    };

    const getStatusClass = (resourceType, permission, role) => {
      if (role === 'tech') {
        return 'text-success text-opacity-50';
      }

      if (role === 'admin' && !props.canEditAdmin) {
        if (
          localPermissions.value &&
          localPermissions.value[role] &&
          localPermissions.value[role][resourceType] &&
          localPermissions.value[role][resourceType][permission]
        ) {
          return 'text-success text-opacity-50';
        }
      }

      if (
        localOldPermissions.value &&
        localOldPermissions.value[role] &&
        localOldPermissions.value[role][resourceType] &&
        localOldPermissions.value[role][resourceType][permission]
      ) {
        if (
          localPermissions.value &&
          localPermissions.value[role] &&
          localPermissions.value[role][resourceType] &&
          localPermissions.value[role][resourceType][permission]
        ) {
          return 'text-success';
        }

        return 'text-warning';
      }

      if (
        localPermissions.value &&
        localPermissions.value[role] &&
        localPermissions.value[role][resourceType] &&
        localPermissions.value[role][resourceType][permission]
      ) {
        return 'text-info';
      }

      if (permissionIsEditable(resourceType, permission, role)) {
        return 'text-inactive';
      }

      return 'text-danger text-opacity-50';
    };

    const permissionIsEditable = (resourceType, permission, role) => {
      if (role === 'admin' && !props.canEditAdmin) {
        return false;
      }

      return (
        localPermissions.value &&
        localPermissions.value[role] &&
        localPermissions.value[role][resourceType] &&
        localPermissions.value[role][resourceType][permission] !== null
      );
    };

    const togglePermission = (resourceType, permission, role) => {
      if (!permissionIsEditable(resourceType, permission, role)) {
        return;
      }

      if (!localPermissions.value[role]) {
        localPermissions.value[role] = {};
      }
      if (!localPermissions.value[role][resourceType]) {
        localPermissions.value[role][resourceType] = {};
      }
      if (!localPermissions.value[role][resourceType][permission]) {
        localPermissions.value[role][resourceType][permission] = true;
      } else {
        localPermissions.value[role][resourceType][permission] = false;
      }
    };

    const updatePermissions = (route) => {
      localOldPermissions.value = {};
      for (let role in localPermissions.value) {
        localOldPermissions.value[role] = {};
        for (let resourceType in localPermissions.value[role]) {
          localOldPermissions.value[role][resourceType] = {};
          for (let permission in localPermissions.value[role][resourceType]) {
            localOldPermissions.value[role][resourceType][permission] =
              localPermissions.value[role][resourceType][permission];
          }
        }
      }
      router.patch(route(`settings.access.update`), localPermissions.value, {
        only: ['errors'],
      });
    };

    return {
      getRoleBackgroundColor,
      getStatusClass,
      localOldPermissions,
      permissionIsEditable,
      togglePermission,
      updatePermissions,
    };
  },
};
</script>
